<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PlanMapCanvas from "./components/PlanMapCanvas.vue";
import PlanMapTimelinePanel from "./components/PlanMapTimelinePanel.vue";
import { ensureAzureMapsSdk } from "../../../maps/azure-maps-sdk";
import { createPlanMapController } from "../../../maps/plan-map-controller";
import {
  getAccountMapStyle,
  resolveMapStyle,
} from "../../../maps/map-style-utils";
import { planStore } from "@/_store/planStore";
import { accountStore } from "@/_store/accountStore";

const props = defineProps({
  planId: {
    type: String,
    default: "",
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  planInfo: {
    type: Object,
    default() {
      return null;
    },
  },
});

const MAP_ZOOM_DEFAULT = 12;
const plannerStore = planStore();
const accountsStore = accountStore();

const mapRootRef = ref(null);
const isLoadingMap = ref(false);
const initError = ref("");
const isTimelineExpanded = ref(false);
const isTimelineVisible = ref(false);
const timelineHeight = ref(172);
const visibleRouteIds = ref(null);
const mapRoutes = ref([]);

let mapController = null;
let renderTicket = 0;
let hasAppliedInitialMapUiDefaults = false;
let hasCustomMapStyleSelection = false;
const currentMapStyle = ref(resolveMapStyle(""));

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function extractRoutesFromApi(response) {
  if (Array.isArray(response?.routes)) return response.routes;
  if (Array.isArray(response?.data?.routes)) return response.data.routes;
  if (Array.isArray(response?.plan?.routes)) return response.plan.routes;
  if (Array.isArray(response?.data?.plan?.routes)) return response.data.plan.routes;
  if (Array.isArray(response)) return response;
  return [];
}

function resolveErrorMessage(error) {
  const messageFromObject = toSafeString(error?.message || error?.detail || error?.error);
  if (messageFromObject) return messageFromObject;
  if (typeof error === "string") return toSafeString(error) || "No se pudo cargar el mapa del plan.";
  if (Array.isArray(error?.messages)) {
    return error.messages.map((message) => toSafeString(message)).filter(Boolean).join(" | ");
  }
  return "No se pudo cargar el mapa del plan.";
}

function buildContract(routesValue) {
  return {
    zoom: MAP_ZOOM_DEFAULT,
    routes: Array.isArray(routesValue) ? routesValue : [],
  };
}

async function ensureMapController() {
  if (mapController) {
    return mapController;
  }

  if (!mapRootRef.value) {
    return null;
  }

  await ensureAzureMapsSdk();
  mapController = createPlanMapController({
    rootElement: mapRootRef.value,
    initialSubscriptionKey: process.env.VUE_APP_AZURE_MAPS_SUBSCRIPTION_KEY || "",
    initialStyle: currentMapStyle.value,
    sampleContract: buildContract([]),
  });

  return mapController;
}

async function renderMapForActivePlan() {
  if (!props.isActive) {
    renderTicket += 1;
    isLoadingMap.value = false;
    return;
  }

  const currentTicket = ++renderTicket;
  isLoadingMap.value = true;
  initError.value = "";

  try {
    await nextTick();
    const controller = await ensureMapController();
    if (!controller || currentTicket !== renderTicket) {
      return;
    }

    const normalizedPlanId = toSafeString(props.planId);
    const mapResponse = normalizedPlanId
      ? await plannerStore.getPlanMapForKanban(normalizedPlanId)
      : { routes: [] };
    if (currentTicket !== renderTicket) {
      return;
    }

    mapRoutes.value = extractRoutesFromApi(mapResponse);

    if (!hasAppliedInitialMapUiDefaults && typeof controller.setTrafficEnabled === "function") {
      controller.setTrafficEnabled(false);
      hasAppliedInitialMapUiDefaults = true;
    }
    await controller.setContract(buildContract(mapRoutes.value));
    await nextTick();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (currentTicket !== renderTicket || !mapController) {
          return;
        }
        if (typeof mapController.resize === "function") {
          mapController.resize();
        }
      });
    });

    if (currentTicket !== renderTicket) {
      return;
    }

    if (Array.isArray(visibleRouteIds.value)) {
      controller.setVisibleRouteIds(visibleRouteIds.value);
    }
  } catch (error) {
    if (currentTicket !== renderTicket) {
      return;
    }

    initError.value = resolveErrorMessage(error);
  } finally {
    if (currentTicket === renderTicket) {
      isLoadingMap.value = false;
    }
  }
}

function onZoomIn() {
  mapController?.zoomIn();
}

function onZoomOut() {
  mapController?.zoomOut();
}

function onFit() {
  mapController?.resetView();
}

function onStyleChange(style) {
  const nextStyle = resolveMapStyle(style);
  if (!nextStyle || nextStyle === currentMapStyle.value) {
    return;
  }

  hasCustomMapStyleSelection = true;
  currentMapStyle.value = nextStyle;
  renderTicket += 1;

  if (mapController && typeof mapController.destroy === "function") {
    mapController.destroy();
  }

  mapController = null;
  void renderMapForActivePlan();
}

onMounted(() => {
  currentMapStyle.value = getAccountMapStyle(accountsStore.account);
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }
});

watch(
  () => accountsStore.account,
  (value) => {
    if (!value || hasCustomMapStyleSelection) return;
    const nextStyle = getAccountMapStyle(value);
    if (nextStyle === currentMapStyle.value) return;
    currentMapStyle.value = nextStyle;
    renderTicket += 1;
    if (mapController && typeof mapController.destroy === "function") {
      mapController.destroy();
    }
    mapController = null;
    if (props.isActive) {
      void renderMapForActivePlan();
    }
  }
);

function onTimelineExpandedChange(value) {
  isTimelineExpanded.value = Boolean(value);
}

function onTimelineHeightChange(height) {
  timelineHeight.value = Number.isFinite(height) ? Math.round(height) : 172;
}


function setTimelineVisible(value) {
  isTimelineVisible.value = Boolean(value);
  if (!isTimelineVisible.value) {
    isTimelineExpanded.value = false;
  }
}

function onTimelineVisibleRoutesChange(payload) {
  const nextRouteIds = Array.isArray(payload?.routeIds)
    ? payload.routeIds.map((routeId) => toSafeString(routeId)).filter(Boolean)
    : [];

  visibleRouteIds.value = nextRouteIds;

  if (!mapController || !props.isActive) {
    return;
  }

  mapController.setVisibleRouteIds(nextRouteIds);
}

watch(
  [() => props.isActive, () => props.planId, () => props.planInfo],
  ([isActive]) => {
    if (!isActive) {
      renderTicket += 1;
      isLoadingMap.value = false;
      return;
    }

    void renderMapForActivePlan();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  renderTicket += 1;

  if (mapController && typeof mapController.destroy === "function") {
    mapController.destroy();
  }

  mapController = null;
});
</script>

<template>
  <div ref="mapRootRef" class="plan-map-tab">
    <plan-map-canvas
      :is-loading-map="isLoadingMap"
      :init-error="initError"
      :is-timeline-expanded="isTimelineExpanded"
      :is-timeline-visible="isTimelineVisible"
      :timeline-height="timelineHeight"
      :map-style="currentMapStyle"
      @timeline-visibility-change="setTimelineVisible"
      @zoom-in="onZoomIn"
      @zoom-out="onZoomOut"
      @fit="onFit"
      @style-change="onStyleChange"
    >
      <plan-map-timeline-panel
        v-if="isTimelineVisible"
        class="map-timeline-overlay"
        :routes="mapRoutes"
        @expanded-change="onTimelineExpandedChange"
        @height-change="onTimelineHeightChange"
        @visible-routes-change="onTimelineVisibleRoutesChange"
      />
    </plan-map-canvas>
  </div>
</template>

<style scoped lang="scss">
.plan-map-tab {
  background: #eef2f8;
  border: 1px solid #dce4f0;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.map-timeline-overlay {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  z-index: 10;
}
</style>
